/**
 * COSO Compliance Service APIs
 * Handles COSO Compliance notes and documents operations
 */

import { apiRequest } from "./apiClient";
import { EmptyResponse, SyncfusionGridParams, SyncfusionSearchParam } from "./types";

// ========================================
// Types & Interfaces
// ========================================

/**
 * COSO Compliance Note from API
 */
export interface COSOComplianceNote {
  Id: string;
  PrincipleNumber: number;
  Content: string;
}

/**
 * Parsed COSO Compliance Note for frontend use
 */
export interface ParsedCOSONote {
  id: string;
  principleNumber: number;
  content: string;
}

/**
 * COSO Compliance Document from API
 */
export interface COSOComplianceDocument {
  Id: string;
  FileName: string;
  FileSize: number;
  CreatedAt: string;
}

/**
 * Parsed COSO Document for frontend use
 */
export interface ParsedCOSODocument {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'other';
  createdAt: string;
}

/**
 * API Response for notes grid
 */
export interface COSONotesGridResponse {
  result: COSOComplianceNote[];
  count: number;
}

/**
 * API Response for documents grid
 */
export interface COSODocumentsGridResponse {
  result: COSOComplianceDocument[];
  count: number;
}

/**
 * Request body for updating compliance note
 */
export interface UpdateCOSONoteRequest {
  annualReportId: string;
  principleNumber: number;
  content: string;
}

// ========================================
// Helper Functions
// ========================================

/**
 * Parse COSO note from API format to frontend format
 */
export const parseCOSONote = (note: COSOComplianceNote): ParsedCOSONote => ({
  id: note.Id,
  principleNumber: note.PrincipleNumber,
  content: note.Content,
});

/**
 * Parse array of COSO notes
 */
export const parseCOSONotes = (notes: COSOComplianceNote[]): ParsedCOSONote[] => 
  notes.map(parseCOSONote);

/**
 * Get file type from filename
 */
const getFileType = (fileName: string): ParsedCOSODocument['fileType'] => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'doc':
      return 'doc';
    case 'docx':
      return 'docx';
    case 'xls':
      return 'xls';
    case 'xlsx':
      return 'xlsx';
    default:
      return 'other';
  }
};

/**
 * Parse COSO document from API format to frontend format
 */
export const parseCOSODocument = (doc: COSOComplianceDocument): ParsedCOSODocument => ({
  id: doc.Id,
  fileName: doc.FileName,
  fileSize: doc.FileSize,
  fileType: getFileType(doc.FileName),
  createdAt: doc.CreatedAt,
});

/**
 * Parse array of COSO documents
 */
export const parseCOSODocuments = (docs: COSOComplianceDocument[]): ParsedCOSODocument[] =>
  docs.map(parseCOSODocument);

/**
 * Format file size to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// ========================================
// Compliance Notes APIs
// ========================================

/**
 * Get all COSO compliance notes for an annual report
 * POST /api/v1/coso-compliance/notes/index-grid/{annualReportId}
 * 
 * @param annualReportId - Annual report ID
 * @param search - Optional search params
 */
export const getCOSOComplianceNotes = async (
  annualReportId: string,
  search?: SyncfusionSearchParam[]
): Promise<{ data: ParsedCOSONote[]; totalCount: number }> => {
  try {
    const gridParams: SyncfusionGridParams = {
      skip: 0,
      take: 1000, // Get all notes (max 17 principles)
      requiresCounts: true,
      ...(search && { search }),
    };

    const response = await apiRequest<COSONotesGridResponse, SyncfusionGridParams>(
      'POST',
      `/coso-compliance/notes/index-grid/${annualReportId}`,
      gridParams,
      true
    );

    return {
      data: parseCOSONotes(response.result || []),
      totalCount: response.count || 0,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch COSO compliance notes',
    };
  }
};

/**
 * Update COSO compliance note content
 * POST /api/v1/coso-compliance
 * Creates new entity if none exists
 */
export const updateCOSOComplianceNote = async (
  annualReportId: string,
  principleNumber: number,
  content: string
): Promise<EmptyResponse> => {
  try {
    const requestBody: UpdateCOSONoteRequest = {
      annualReportId,
      principleNumber,
      content,
    };

    return await apiRequest<EmptyResponse, UpdateCOSONoteRequest>(
      'POST',
      `/coso-compliance`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update COSO compliance note',
    };
  }
};

// ========================================
// Documents APIs
// ========================================

/**
 * Get all COSO compliance documents for an annual report
 * POST /api/v1/coso-compliance/downloads/index-grid/{annualReportId}
 */
export const getCOSOComplianceDocuments = async (
  annualReportId: string
): Promise<{ data: ParsedCOSODocument[]; totalCount: number }> => {
  try {
    const gridParams: SyncfusionGridParams = {
      skip: 0,
      take: 1000, // Get all documents
      requiresCounts: true,
    };

    const response = await apiRequest<COSODocumentsGridResponse, SyncfusionGridParams>(
      'POST',
      `/coso-compliance/downloads/index-grid/${annualReportId}`,
      gridParams,
      true
    );

    return {
      data: parseCOSODocuments(response.result || []),
      totalCount: response.count || 0,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch COSO compliance documents',
    };
  }
};

